// ---------------------------------------------------------------
// TagAlong alert preferences (per device): which alerts are on, how each is
// delivered (text / call / both), and the customer's own custom alerts.
// Delivery channel is a preference; texts/calls go out for real once telephony
// is wired. Call alerts are advised for HIGH-priority alerts only.
// ---------------------------------------------------------------

const KEY = 'dd-alert-prefs';

// built-in alerts the tracker already produces. `threshold` = a number the
// customer can set (saved to the device so the watcher uses it).
export const BUILTIN_ALERTS = [
  { key: 'crash', label: 'Crash / impact', priority: 'critical' },
  { key: 'towTheft', label: 'Tow-away / theft', priority: 'critical' },
  { key: 'highwayStop', label: 'Stopped on the highway', priority: 'critical', threshold: { attr: 'highwayStopMin', unit: 'min', def: 17 } },
  { key: 'offline', label: 'Tracker unplugged while in use', priority: 'high' },
  { key: 'overspeed', label: 'Over-speed (hard limit)', priority: 'high', threshold: { attr: 'speedMaxMph', unit: 'mph', def: 85 } },
  { key: 'speeding', label: 'Speeding warning', priority: 'high', threshold: { attr: 'speedWarnMph', unit: 'mph', def: 70 } },
  { key: 'checkEngine', label: 'Check-engine / trouble code', priority: 'medium' },
  { key: 'aggressiveRpm', label: 'Aggressive RPM (hard revving)', priority: 'medium', threshold: { attr: 'rpmAlertRpm', unit: 'rpm', def: 4000 } },
  { key: 'hardBraking', label: 'Hard braking', priority: 'medium' },
  // Computed from how fast speed climbs between fixes, so it works even on
  // trackers that don't emit the event. Threshold is mph gained per second —
  // higher = only the hardest launches alert. 8 ≈ a spirited 0–60 in ~7.5s.
  { key: 'hardAccel', label: 'Hard / jumpy acceleration', priority: 'medium', threshold: { attr: 'accelMphPerSec', unit: 'mph/s', def: 8 } },
  { key: 'lowBattery', label: 'Low battery', priority: 'medium', threshold: { attr: 'lowVoltage', unit: 'V', def: 12.2 } },
  { key: 'lowFuel', label: 'Low fuel', priority: 'low', threshold: { attr: 'lowFuelPct', unit: '%', def: 15 } },
  // Fires AFTER fuelling finishes, reporting the level it filled to — useful
  // for spotting a fill that's smaller than expected, or fuel going missing
  // between fills. Threshold is the jump size that counts as a refuel, so a
  // sloshing tank on a hill doesn't read as one.
  { key: 'refuel', label: 'Refuelled (reports new level)', priority: 'low', threshold: { attr: 'refuelMinPct', unit: '% jump', def: 8 } },
  { key: 'idle', label: 'Idling too long (parked, running)', priority: 'low', threshold: { attr: 'idleAlertMin', unit: 'min', def: 15 } },
  { key: 'arrival', label: 'Arrivals / departures at saved places', priority: 'low' },
  { key: 'ignitionOn', label: 'Car turned on', priority: 'low' },
];

export const HIGH_PRIORITY = ['critical', 'high'];

// every metric the OBD reader / tracker exposes, for building custom alerts
export const CUSTOM_METRICS = [
  { key: 'speed', label: 'Speed', unit: 'mph' },
  { key: 'fuel', label: 'Fuel level', unit: '%' },
  { key: 'battery', label: 'Battery voltage', unit: 'V' },
  { key: 'rpm', label: 'Engine RPM', unit: 'rpm' },
  { key: 'coolant', label: 'Coolant temp', unit: '°C' },
  { key: 'engineLoad', label: 'Engine load', unit: '%' },
  { key: 'throttle', label: 'Throttle position', unit: '%' },
  { key: 'ambient', label: 'Outside temp', unit: '°C' },
  { key: 'odometer', label: 'Odometer', unit: 'mi' },
  { key: 'dtc', label: 'Trouble codes', unit: 'codes' },
];

// Delivery defaults to IN-APP (app notifications). Text/call stay as options for
// later (when telephony is connected) but are off by default to keep costs down.
const DEFAULTS = () => {
  const builtins = {};
  BUILTIN_ALERTS.forEach((a) => { builtins[a.key] = { on: true, channel: 'app' }; });
  return { builtins, custom: [] };
};

export function getAlertPrefs(deviceId) {
  try {
    const all = JSON.parse(localStorage.getItem(KEY)) || {};
    const saved = all[deviceId] || {};
    const base = DEFAULTS();
    const builtins = { ...base.builtins };
    Object.keys(saved.builtins || {}).forEach((k) => { builtins[k] = { ...builtins[k], ...saved.builtins[k] }; });
    return { builtins, custom: Array.isArray(saved.custom) ? saved.custom : [] };
  } catch { return DEFAULTS(); }
}

export function saveAlertPrefs(deviceId, prefs) {
  let all = {};
  try { all = JSON.parse(localStorage.getItem(KEY)) || {}; } catch { /* ignore */ }
  all[deviceId] = prefs;
  localStorage.setItem(KEY, JSON.stringify(all));
}
